import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const SearchContext = createContext();

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

export const SearchProvider = ({ children }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedAuthor, setSelectedAuthor] = useState('');
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [sortBy, setSortBy] = useState('date-desc'); // date-desc, date-asc, title-asc, relevance
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();

  // Parse URL search params on load
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get('q') || '';
    const tags = params.get('tags') ? params.get('tags').split(',') : [];
    const author = params.get('author') || '';
    const sort = params.get('sort') || 'date-desc';
    
    setSearchQuery(query);
    setSelectedTags(tags);
    setSelectedAuthor(author);
    setSortBy(sort);
  }, [location.search]);

  // Update URL when search state changes
  const updateURL = (newParams) => {
    const params = new URLSearchParams();
    
    if (newParams.query || searchQuery) {
      params.set('q', newParams.query ?? searchQuery);
    }
    if ((newParams.tags || selectedTags).length > 0) {
      params.set('tags', (newParams.tags || selectedTags).join(','));
    }
    if (newParams.author || selectedAuthor) {
      params.set('author', newParams.author ?? selectedAuthor);
    }
    if ((newParams.sort || sortBy) !== 'date-desc') {
      params.set('sort', newParams.sort ?? sortBy);
    }

    const newSearch = params.toString();
    const newPath = location.pathname + (newSearch ? `?${newSearch}` : '');
    
    if (newPath !== location.pathname + location.search) {
      navigate(newPath, { replace: true });
    }
  };

  const handleSearchChange = (query) => {
    setSearchQuery(query);
    updateURL({ query });
  };

  const handleTagsChange = (tags) => {
    setSelectedTags(tags);
    updateURL({ tags });
  };

  const handleAuthorChange = (author) => {
    setSelectedAuthor(author);
    updateURL({ author });
  };

  const handleSortChange = (sort) => {
    setSortBy(sort);
    updateURL({ sort });
  };

  const addTag = (tag) => {
    if (!selectedTags.includes(tag)) {
      const newTags = [...selectedTags, tag];
      handleTagsChange(newTags);
    }
  };

  const removeTag = (tag) => {
    const newTags = selectedTags.filter(t => t !== tag);
    handleTagsChange(newTags);
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedTags([]);
    setSelectedAuthor('');
    setDateRange({ start: null, end: null });
    setSortBy('date-desc');
    navigate(location.pathname, { replace: true });
  };

  const hasActiveFilters = () => {
    return searchQuery || selectedTags.length > 0 || selectedAuthor || 
           dateRange.start || dateRange.end || sortBy !== 'date-desc';
  };

  const value = {
    // State
    searchQuery,
    selectedTags,
    selectedAuthor,
    dateRange,
    sortBy,
    isSearchOpen,
    
    // Actions
    setSearchQuery: handleSearchChange,
    setSelectedTags: handleTagsChange,
    setSelectedAuthor: handleAuthorChange,
    setDateRange,
    setSortBy: handleSortChange,
    setIsSearchOpen,
    addTag,
    removeTag,
    clearAllFilters,
    hasActiveFilters,
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};